import React from 'react';
import { Text, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import i18n from 'i18n-js';

import { WithColor } from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { OperationSearchItem, RecipeOperationSearchItem } from '@interfaces/search';

import LinePopupButton from './LinePopupButton';
import { formatTime } from '@components/productionInformation/baseWithERP/helpers/formatTime';
import { getLinkedText } from '@components/productionInformation/baseWithERP/helpers/linkedText';

type OperationItem = OperationSearchItem | RecipeOperationSearchItem;

interface OperationsListProps<TItem extends OperationItem> extends WithColor {
  operations: TItem[];
  ncFileName?: string;
  onLinkPress: (item: TItem) => void;
  keyExtractor: (item: TItem) => string;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const OperationsList = <TItem extends OperationItem>({
  C,
  operations,
  ncFileName,
  onLinkPress,
  keyExtractor,
  scale = initialScale,
  font = initialFont,
}: OperationsListProps<TItem>) => {
  if (!operations?.length) {
    return (
      <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), marginHorizontal: 'auto', marginTop: scale(10) }]}>
        {i18n.t('Operations not found')}
      </Text>
    );
  }

  return (
    <FlashList
      data={operations}
      keyExtractor={keyExtractor}
      contentContainerStyle={{ paddingHorizontal: scale(18) }}
      ListFooterComponent={<View style={{ height: scale(15) }} />}
      renderItem={({ item, index }) => {
        const title = item.operationName;
        const setupTime = `${formatTime(item?.setupTimeSeconds)} ${i18n.t('Setup')}`;
        const cycleTime = `${formatTime(item?.operationTimeSeconds)} ${i18n.t('Cycle')}`;
        const text = `${setupTime} | ${cycleTime}`;

        const linkedText = getLinkedText(item);
        const popupText = `${item.description || ''}\n${linkedText}`;
        const popupAdditionalText = item?.linkedNcFiles?.length
          ? i18n.t('Also Link {{name}} to this Operation', { name: ncFileName })
          : ' ';

        return (
          <LinePopupButton
            {...{
              scale,
              font,
              C,
              orderNo: item.operationNumber,
              title,
              text,
              popupText,
              popupBtnText: 'Link',
              popupAdditionalText,
              popupBtnHandlePress: () => onLinkPress(item),
              status: item.isActive ? 'running' : 'completed',
              shadow: false,
              borderBottom: index !== operations?.length - 1,
              borderRadiusTop: index === 0,
              borderRadiusBottom: index === operations?.length - 1,
            }}
          />
        );
      }}
    />
  );
};

export default OperationsList;
